import { useDispatch } from 'react-redux'
import { useToast } from '@homebotapp/hb-react-component-catalog'
import {
  createTeamMembership,
  createTeamMembershipWithCustomer,
  replaceTeamMembership,
  replaceTeamMembershipWithCustomer
} from '../store/slices/homebotNetwork/clientTeamMemberships'
import { getHbnCustomerProfileByEmailRequest } from '../api/mikasa/homebotNetwork/requests'
import { AppDispatch } from '../store'
import { PartnerType } from '../Components/shared/VerificationFlow'

interface UseTeamMembershipProps {
  clientId: string
  clientTeamMembershipId?: string
  onSuccess?: () => void
}

interface ApiError {
  response?: {
    status?: number
    data?: {
      error?: string
    }
  }
}

export interface FormData {
  firstName: string
  lastName: string
  email: string
  type: PartnerType
}

interface CustomerProfileResponse {
  id: string
  firstName: string
  lastName: string
  email: string
  stateLicense: string | null
  type: string
  photoUri: string
  corporateProfile: {
    name: string
    avatarUri: string | null
  }
}

export const useTeamMembership = ({ clientId, clientTeamMembershipId, onSuccess }: UseTeamMembershipProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const toast = useToast()

  const handleError = (error: ApiError) => {
    const errorMessage = error.response?.data?.error || 'Failed to add agent. Please try again.'
    toast({
      title: 'Error',
      description: errorMessage,
      status: 'error'
    })
  }

  const createMembership = async (data: FormData) => {
    if (!clientId) return

    try {
      const result = await dispatch(
        createTeamMembership({
          clientId,
          preferredAgentFirstName: data.firstName,
          preferredAgentLastName: data.lastName,
          preferredAgentEmail: data.email,
          isVerified: true,
          type: data.type
        })
      )

      if (result.type.endsWith('/fulfilled')) {
        const successMessage =
          data.type === 'LoanOfficer' ? 'Loan officer added successfully' : 'Agent added successfully'
        toast({
          title: 'Success',
          description: successMessage,
          status: 'success'
        })
        onSuccess?.()
      }
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  const createMembershipWithCustomer = async (customerProfileId: string) => {
    if (!clientId) return

    try {
      const result = await dispatch(
        createTeamMembershipWithCustomer({
          clientId,
          customerProfileId,
          isVerified: true
        })
      )

      if (result.type.endsWith('/fulfilled')) {
        toast({
          title: 'Success',
          description: 'Agent added successfully',
          status: 'success'
        })
        onSuccess?.()
      }
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  const replaceMembership = async (data: FormData) => {
    if (!clientId || !clientTeamMembershipId) return

    try {
      const result = await dispatch(
        replaceTeamMembership({
          clientId,
          clientTeamMembershipId: clientTeamMembershipId,
          preferredAgentFirstName: data.firstName,
          preferredAgentLastName: data.lastName,
          preferredAgentEmail: data.email,
          type: data.type,
          isVerified: true
        })
      )

      if (result.type.endsWith('/fulfilled')) {
        const successMessage =
          data.type === 'LoanOfficer' ? 'Loan officer replaced successfully' : 'Agent replaced successfully'
        toast({
          title: 'Success',
          description: successMessage,
          status: 'success'
        })
        onSuccess?.()
      }
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  const replaceMembershipWithCustomer = async (customerProfileId: string) => {
    if (!clientId || !clientTeamMembershipId) return

    try {
      const result = await dispatch(
        replaceTeamMembershipWithCustomer({
          clientId,
          clientTeamMembershipId: clientTeamMembershipId,
          customerProfileId,
          isVerified: true
        })
      )

      if (result.type.endsWith('/fulfilled')) {
        toast({
          title: 'Success',
          description: 'Replaced successfully',
          status: 'success'
        })
        onSuccess?.()
      }
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  const checkExistingAgent = async (email: string): Promise<CustomerProfileResponse | null> => {
    try {
      const response = await getHbnCustomerProfileByEmailRequest(email)
      const customerProfile = response.data.data

      if (customerProfile) {
        return {
          id: customerProfile.id,
          firstName: customerProfile.first_name,
          lastName: customerProfile.last_name,
          email: customerProfile.email,
          stateLicense: customerProfile.state_license,
          type: customerProfile.type,
          photoUri: customerProfile.photo_uri,
          corporateProfile: {
            name: customerProfile.corporate_profile.name,
            avatarUri: customerProfile.corporate_profile.avatar_uri
          }
        }
      }
      return null
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  }

  return {
    createMembership,
    createMembershipWithCustomer,
    replaceMembership,
    replaceMembershipWithCustomer,
    checkExistingAgent,
    handleError
  }
}
